import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Icons,
  IconButton,
  iconSize,
  MentionText,
  MentionTextField,
  palette,
  Stack,
  Tooltip,
  Typography,
  useConfirm,
  useSnackbar,
} from '@munifocus/ui';
import { NOTE_BODY_MAX, type FileNoteDto } from '@munifocus/contracts-gov';
import { useCreateNote, useDeleteNote, useFileNotes, useUpdateNote } from '../../lib/teamwork-api';
import { intlLocale } from '../../lib/relative-time';
import { apiErrorMessage } from '../../lib/mutation-toast';
import { useFileMentions } from './useFileMentions';
import { frenchFirstOfMonth } from '@munifocus/utils';

/** The note id a `#note-<id>` deep link points at, if any. */
export function noteIdFromHash(hash: string): string | null {
  const match = /^#note-([0-9a-f-]{36})$/i.exec(hash);
  return match ? match[1] : null;
}

function NoteComposer({
  fileId,
  initial,
  submitLabel,
  pending,
  onSubmit,
  onCancel,
}: {
  fileId: string;
  initial?: FileNoteDto;
  submitLabel: string;
  pending: boolean;
  onSubmit: (body: string, mentionUserIds: string[]) => Promise<unknown>;
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const [body, setBody] = useState(initial?.body ?? '');
  const mentions = useFileMentions(fileId);
  const { reset } = mentions;

  useEffect(() => {
    reset(initial?.mentions ?? []);
  }, [initial, reset]);

  const trimmed = body.trim();
  const submit = async () => {
    if (!trimmed || pending) return;
    await onSubmit(trimmed, mentions.mentionIdsFor(trimmed));
    if (!initial) {
      setBody('');
      reset();
    }
  };

  return (
    <Stack spacing={1}>
      <MentionTextField
        value={body}
        onChange={(v) => setBody(v.slice(0, NOTE_BODY_MAX))}
        suggestions={mentions.suggestions}
        onPick={mentions.onPick}
        listLabel={t('gov.teamwork.mentions.listLabel')}
        label={initial ? t('gov.teamwork.notes.editLabel') : t('gov.teamwork.notes.newLabel')}
        placeholder={t('gov.teamwork.notes.placeholder')}
        minRows={2}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            void submit();
          }
        }}
      />
      <Stack
        direction="row"
        spacing={1}
        sx={{ justifyContent: 'space-between', alignItems: 'center' }}
      >
        <Typography variant="caption" sx={{ color: palette.text.muted }}>
          {t('gov.teamwork.notes.mentionHint')}
        </Typography>
        <Stack direction="row" spacing={1}>
          {onCancel && (
            <Button size="small" onClick={onCancel}>
              {t('common.actions.cancel')}
            </Button>
          )}
          <Button
            size="small"
            variant="contained"
            disabled={!trimmed || pending}
            onClick={() => void submit()}
          >
            {submitLabel}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
}

/**
 * Running notes on a file, newest first, with @mentions.
 *
 * Only colleagues who can read the file are offered in the picker; whoever is
 * mentioned is notified (in the inbox always, by email and phone where their
 * municipality and their own settings allow). A `#note-<id>` link — what a
 * mention notification opens — scrolls to and outlines that note.
 */
export function FileNotesPanel({ fileId }: { fileId: string }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const snackbar = useSnackbar();
  const confirm = useConfirm();
  const notes = useFileNotes(fileId);
  const create = useCreateNote(fileId);
  const update = useUpdateNote(fileId);
  const remove = useDeleteNote(fileId);
  const [editing, setEditing] = useState<string | null>(null);
  const highlighted = noteIdFromHash(location.hash);
  const locale = intlLocale(i18n.language);

  useEffect(() => {
    const panelRequested = new URLSearchParams(location.search).get('panel') === 'notes';
    if (!notes.data || (!highlighted && !panelRequested)) return;
    const target = document.getElementById(highlighted ? `note-${highlighted}` : 'file-notes');
    target?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }, [notes.data, highlighted, location.search]);

  const format = (iso: string) =>
    frenchFirstOfMonth(
      new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
        new Date(iso),
      ),
    );

  return (
    <Stack spacing={1.5} id="file-notes">
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Icons.StickyNote size={iconSize.md} aria-hidden />
        <Typography variant="h6">{t('gov.teamwork.notes.title')}</Typography>
      </Stack>
      <Typography variant="body2" sx={{ color: palette.text.muted }}>
        {t('gov.teamwork.notes.hint')}
      </Typography>

      <NoteComposer
        fileId={fileId}
        submitLabel={t('gov.teamwork.notes.add')}
        pending={create.isPending}
        onSubmit={(body, mentionUserIds) =>
          create.mutateAsync({ body, mentionUserIds }).then(
            () => snackbar.success(t('gov.teamwork.notes.added')),
            (err) => snackbar.error(apiErrorMessage(err, t('gov.teamwork.notes.saveError'))),
          )
        }
      />

      {notes.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} aria-label={t('gov.teamwork.notes.loading')} />
        </Box>
      )}
      {notes.isError && <Alert severity="error">{t('gov.teamwork.notes.loadError')}</Alert>}
      {notes.data?.length === 0 && (
        <Typography variant="body2" sx={{ color: palette.text.muted }}>
          {t('gov.teamwork.notes.empty')}
        </Typography>
      )}

      <Stack spacing={1} component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
        {notes.data?.map((note) => (
          <Box
            component="li"
            key={note.id}
            id={`note-${note.id}`}
            sx={{
              border: `1px solid ${
                highlighted === note.id ? palette.brand.accent : palette.border.line
              }`,
              borderRadius: 1.5,
              p: 1.5,
              backgroundColor: note.deletedAt ? palette.background.parch : 'transparent',
            }}
          >
            <Stack
              direction="row"
              sx={{ justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {note.author.name || t('gov.activity.unknownActor')}
              </Typography>
              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                <Typography variant="caption" sx={{ color: palette.text.muted }}>
                  {format(note.createdAt)}
                  {note.editedAt && !note.deletedAt ? ` · ${t('gov.teamwork.notes.edited')}` : ''}
                </Typography>
                {note.mine && !note.deletedAt && (
                  <>
                    <Tooltip title={t('common.actions.edit')}>
                      <IconButton
                        size="small"
                        aria-label={t('gov.teamwork.notes.editAria')}
                        onClick={() => setEditing(note.id)}
                      >
                        <Icons.Pencil size={iconSize.sm} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('gov.teamwork.notes.delete')}>
                      <IconButton
                        size="small"
                        aria-label={t('gov.teamwork.notes.deleteAria')}
                        onClick={async () => {
                          const ok = await confirm({
                            title: t('gov.teamwork.notes.deleteTitle'),
                            body: t('gov.teamwork.notes.deleteBody'),
                            confirmLabel: t('gov.teamwork.notes.delete'),
                            cancelLabel: t('common.actions.cancel'),
                            tone: 'danger',
                          });
                          if (ok) remove.mutate(note.id);
                        }}
                      >
                        <Icons.Trash2 size={iconSize.sm} />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Stack>
            </Stack>
            {editing === note.id ? (
              <Box sx={{ mt: 1 }}>
                <NoteComposer
                  fileId={fileId}
                  initial={note}
                  submitLabel={t('common.actions.save')}
                  pending={update.isPending}
                  onCancel={() => setEditing(null)}
                  onSubmit={(body, mentionUserIds) =>
                    update.mutateAsync({ id: note.id, body, mentionUserIds }).then(
                      () => setEditing(null),
                      (err) =>
                        snackbar.error(apiErrorMessage(err, t('gov.teamwork.notes.saveError'))),
                    )
                  }
                />
              </Box>
            ) : note.deletedAt ? (
              <Typography variant="body2" sx={{ fontStyle: 'italic', color: palette.text.muted }}>
                {t('gov.teamwork.notes.deleted')}
              </Typography>
            ) : (
              <Typography
                variant="body2"
                sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', mt: 0.5 }}
              >
                <MentionText
                  text={note.body}
                  mentions={note.mentions.map((m) => ({ id: m.userId, label: m.displayName }))}
                />
              </Typography>
            )}
          </Box>
        ))}
      </Stack>
    </Stack>
  );
}
